// Inline OTP + Car + Razorpay checkout modal. Stays on the landing URL
// (no /me redirect) to maximize conversion. Fires Pixel/CAPI/Ads events
// at each step: InitiateCheckout → Lead (OTP verify) → Purchase (paid).
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Loader2, ShieldCheck, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  customerApi, setCustomerToken, getCustomerToken, loadRazorpayScript,
  isValidPhone, errText, CAR_BRANDS,
} from "@/lib/api";
import {
  trackInitiateCheckout, trackOtpVerified, trackCompleteRegistration,
  trackPurchase, setAdvancedMatching,
} from "@/lib/pixel";
import { getAttribution } from "@/lib/utm";
import { currentVariant } from "@/lib/abTest";

const STEPS = { PHONE: "phone", OTP: "otp", CAR: "car", PAY: "pay", DONE: "done" };

// Display-side package metadata so we can show price + label per tier
// before the order is created. Kept in sync with backend `care_plus.py`.
const PACKAGE_META = {
  care_plus_3m:  { label: "3 Months",  price: 549,  validity_days: 90  },
  care_plus_6m:  { label: "6 Months",  price: 749,  validity_days: 180 },
  care_plus_12m: { label: "12 Months", price: 1249, validity_days: 365 },
};

export default function CPCheckoutModal({ open, onClose, packageId = "care_plus_12m" }) {
  const tier = PACKAGE_META[packageId] || PACKAGE_META.care_plus_12m;
  const [step, setStep] = useState(STEPS.PHONE);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [reg, setReg] = useState("");
  const [customer, setCustomer] = useState(null);

  // If already logged in, skip ahead to car/pay
  useEffect(() => {
    if (!open) return;
    const token = getCustomerToken();
    if (!token) return;
    customerApi.get("/customer/me")
      .then(({ data }) => {
        setCustomer(data.customer);
        setPhone(data.customer.phone);
        setName(data.customer.name);
        if (data.customer.cars?.length) setStep(STEPS.PAY);
        else setStep(STEPS.CAR);
      })
      .catch(() => setCustomerToken(null));
  }, [open]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setBusy(false); setOtp(""); setBrand(""); setModel(""); setReg("");
      if (!getCustomerToken()) setStep(STEPS.PHONE);
    }
  }, [open]);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name");
    if (!isValidPhone(phone)) return toast.error("Enter a valid 10-digit mobile number");
    setBusy(true);
    try {
      // Pre-fire pixel matching with the phone/name now — better Meta match-rates
      setAdvancedMatching({ phone, name });
      await customerApi.post("/customer/otp/send", { phone });
      setStep(STEPS.OTP);
      toast.success("OTP sent! 📲");
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(otp)) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await customerApi.post("/customer/auth/verify", { phone, otp, name: name.trim() });
      setCustomerToken(data.token);
      setCustomer(data.customer);
      trackOtpVerified({ source: "care_plus_offer_lp", phone, name: data.customer?.name });
      if (data.is_new) trackCompleteRegistration({ phone, name: data.customer?.name });
      // Skip car step if customer already has cars on file
      if (data.customer?.cars?.length) setStep(STEPS.PAY);
      else setStep(STEPS.CAR);
    } catch (err) {
      toast.error(errText(err));
    } finally { setBusy(false); }
  };

  const addCarAndPay = async (e) => {
    e?.preventDefault();
    if (!brand) return toast.error("Pick a brand");
    if (!model.trim()) return toast.error("Enter the model");
    setBusy(true);
    try {
      await customerApi.post("/customer/cars", { brand, model, registration: reg });
      setStep(STEPS.PAY);
      // proceed immediately to payment
      await openRazorpay();
    } catch (err) {
      toast.error(errText(err));
      setBusy(false);
    }
  };

  const openRazorpay = async () => {
    setBusy(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Could not load payment provider. Check your internet.");
      const { data } = await customerApi.post("/customer/care-plus/order", {
        variant: currentVariant(),
        package_id: packageId,
      });
      const attr = getAttribution();
      trackInitiateCheckout({ order_id: data.order_id, package_id: packageId, value: tier.price, ...attr });

      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "AutoHQ",
        description: data.name,
        image: "/logo-on-light.png",
        prefill: { name: data.customer_name, contact: data.customer_phone },
        theme: { color: "#D4AF37" },
        notes: {
          utm_source: attr.utm_source || "",
          utm_campaign: attr.utm_campaign || "",
          fbclid: attr.fbclid || "",
          gclid: attr.gclid || "",
          variant: currentVariant(),
          package_id: packageId,
        },
        modal: { ondismiss: () => { setBusy(false); toast("Payment cancelled."); } },
        handler: async (resp) => {
          try {
            await customerApi.post("/customer/care-plus/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            trackPurchase({ value: tier.price, order_id: resp.razorpay_order_id, package_id: packageId });
            toast.success("🎉 Care+ activated!");
            setStep(STEPS.DONE);
          } catch (err) {
            toast.error(errText(err));
          } finally { setBusy(false); }
        },
      });
      rzp.on("payment.failed", (r) => {
        toast.error("Payment failed: " + (r?.error?.description || "unknown"));
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.message || errText(err));
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="cp-checkout-modal"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40 }}
          transition={{ type: "spring", damping: 22, stiffness: 240 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full sm:max-w-md rounded-t-lg sm:rounded-sm overflow-hidden relative my-auto"
        >
          <button
            data-testid="cp-modal-close"
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#FAFAFA] text-[#71717A] z-10"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header */}
          <div className="bg-[#0A0A0A] text-white px-6 py-5">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Activate Care+</p>
            <h3 className="mt-1.5 text-xl font-bold" data-testid="cp-modal-tier-header">
              ₹{tier.price.toLocaleString("en-IN")} · {tier.label}
            </h3>
            <p className="mt-1 text-xs text-white/60">
              {step === STEPS.PHONE && "Step 1 of 3 · Your details"}
              {step === STEPS.OTP && "Step 2 of 3 · Verify OTP"}
              {step === STEPS.CAR && "Step 3 of 3 · Your car"}
              {step === STEPS.PAY && "Final step · Payment"}
              {step === STEPS.DONE && "All done — welcome to Care+"}
            </p>
          </div>

          <div className="p-6">
            {step === STEPS.PHONE && (
              <form onSubmit={sendOtp} className="space-y-4" data-testid="cp-modal-phone-form">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Your Name</Label>
                  <Input
                    data-testid="cp-modal-name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Ravi Kumar"
                    className="mt-2 border-[#E4E4E7]" autoFocus
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Mobile Number</Label>
                  <Input
                    data-testid="cp-modal-phone"
                    value={phone} maxLength={10} inputMode="numeric"
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile" className="mt-2 border-[#E4E4E7]"
                  />
                </div>
                <Button
                  data-testid="cp-modal-send-otp"
                  type="submit" disabled={busy}
                  className="w-full bg-[#0A0A0A] hover:bg-black text-white font-bold py-6"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send OTP"}
                </Button>
                <p className="text-[11px] text-[#71717A] text-center">
                  We&apos;ll text you a 6-digit code. No spam, ever.
                </p>
              </form>
            )}

            {step === STEPS.OTP && (
              <form onSubmit={verifyOtp} className="space-y-4" data-testid="cp-modal-otp-form">
                <p className="text-xs text-[#71717A]">
                  OTP sent to <span className="font-bold text-[#0A0A0A]">+91 {phone}</span>
                </p>
                <Input
                  data-testid="cp-modal-otp"
                  value={otp} maxLength={6} inputMode="numeric"
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="6-digit OTP"
                  className="text-center text-2xl tracking-[0.5em] font-bold py-7 border-[#E4E4E7]"
                  autoFocus
                />
                <Button
                  data-testid="cp-modal-verify"
                  type="submit" disabled={busy}
                  className="w-full bg-[#D4AF37] hover:bg-[#E5C254] text-[#0A0A0A] font-bold py-6"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
                </Button>
                <button
                  type="button" onClick={() => setStep(STEPS.PHONE)}
                  className="block w-full text-xs text-[#71717A] hover:text-[#0A0A0A]"
                >
                  ← Change number
                </button>
              </form>
            )}

            {step === STEPS.CAR && (
              <form onSubmit={addCarAndPay} className="space-y-4" data-testid="cp-modal-car-form">
                <p className="text-sm text-[#52525B]">Tell us about your car. We&apos;ll register coupons against it.</p>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Brand</Label>
                  <Select value={brand} onValueChange={setBrand}>
                    <SelectTrigger data-testid="cp-modal-brand" className="mt-2 border-[#E4E4E7]">
                      <SelectValue placeholder="Pick your car brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAR_BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">Model</Label>
                  <Input
                    data-testid="cp-modal-model"
                    value={model} onChange={(e) => setModel(e.target.value)}
                    placeholder="e.g. Swift, Creta, City"
                    className="mt-2 border-[#E4E4E7]"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-[#52525B]">
                    Reg. No. <span className="text-[#A1A1AA] normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Input
                    data-testid="cp-modal-reg"
                    value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())}
                    placeholder="TS 09 XX 1234"
                    className="mt-2 border-[#E4E4E7] font-mono"
                  />
                </div>
                <Button
                  data-testid="cp-modal-add-car-pay"
                  type="submit" disabled={busy}
                  className="w-full bg-[#D4AF37] hover:bg-[#E5C254] text-[#0A0A0A] font-bold py-6"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Continue to Payment → ₹${tier.price.toLocaleString("en-IN")}`}
                </Button>
              </form>
            )}

            {step === STEPS.PAY && (
              <div className="space-y-4 text-center" data-testid="cp-modal-pay-stage">
                <p className="text-sm text-[#52525B]">
                  Hi <strong className="text-[#0A0A0A]">{customer?.name?.split(" ")[0]}</strong> — let&apos;s complete your payment.
                </p>
                <Button
                  data-testid="cp-modal-pay"
                  onClick={openRazorpay} disabled={busy}
                  className="w-full bg-[#D4AF37] hover:bg-[#E5C254] text-[#0A0A0A] font-extrabold py-7 text-base"
                >
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Razorpay…</> : `Pay ₹${tier.price.toLocaleString("en-IN")} Securely`}
                </Button>
                <p className="text-[11px] text-[#71717A] flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" /> Powered by Razorpay · UPI · Cards · NetBanking
                </p>
              </div>
            )}

            {step === STEPS.DONE && (
              <div className="text-center py-4" data-testid="cp-modal-done">
                <div className="w-14 h-14 mx-auto bg-[#D4AF37]/15 rounded-full flex items-center justify-center">
                  <Check className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-[#0A0A0A]">Care+ Activated!</h3>
                <p className="mt-2 text-sm text-[#52525B]">
                  Your coupons are ready in your wallet. Valid for {tier.validity_days} days.
                </p>
                <Button
                  asChild
                  data-testid="cp-modal-goto-dashboard"
                  className="mt-5 w-full bg-[#0A0A0A] hover:bg-black text-white font-bold py-6"
                >
                  <a href="/me">Open My Dashboard →</a>
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
